import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import dayjs, { type Dayjs } from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { $downloadSepaStatementStore, $initialAccountsStore } from '@lib/store';
import { DateUtils, ValidateUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import { StatementExportFileType } from '@shared/api/rest/statements';
import { DatePickerInput, DateRangePickerInput, Button, Text, Spinner, ErrorMessage } from '@shared/widgets';
import { SelectAccount } from '@shared/widgets/select';
import { ConfirmModal } from '@pages/payments';
import AlertIcon from '@assets/icons/alert.svg';

// const FILE_TYPE_OPTIONS = Object.entries(StatementExportFileType).map((x) => ({ value: x[0], text: x[1] }));

const now = dayjs();
interface IForm {
  account: AccountResponse | null;
  date: { startDate?: Dayjs; endDate?: Dayjs };
  fileType: StatementExportFileType;
}
const DEFAULT_VALUES: IForm = { account: null, date: { startDate: now, endDate: now }, fileType: StatementExportFileType.Pdf };

export const StatementPaymentTab = () => {
  const accountStore = useUnit($initialAccountsStore.store);
  const downloadSepaStatementStore = useUnit($downloadSepaStatementStore.store);

  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

  const { control, handleSubmit, setValue } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (accountStore.data.length > 0) setValue('account', accountStore.data[0]);
  }, [accountStore.data]);

  useEffect(() => {
    if (downloadSepaStatementStore.error) setShowErrorModal(true);
    $downloadSepaStatementStore.events.clear();
  }, [downloadSepaStatementStore.error]);

  const submitHandler = (data: IForm) => {
    $downloadSepaStatementStore.events.downloadSepaStatement({
      accountId: data.account!.id,
      fileType: data.fileType,
      from: DateUtils.formatToIsoString(data.date.startDate!),
      to: DateUtils.formatToIsoString(data.date.endDate!),
    });
  };

  return (
    <form className='flex flex-col w-full' onSubmit={handleSubmit(submitHandler)}>
      {downloadSepaStatementStore.loading && <Spinner position />}
      <div className='flex flex-col bg-white dark:bg-soft-black p-6'>
        <Controller
          name='account'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <SelectAccount
                loading={accountStore.loading}
                balanceFieldName='availableBalance'
                type={AccountType.Payment}
                accounts={accountStore.data!}
                currentAccount={field.value}
                onChange={(account) => field.onChange(account)}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      </div>
      <div className='flex gap-6 mt-6 max-[1140px]:flex-col'>
        <div className='w-full flex flex-col text-center bg-white dark:bg-soft-black p-4'>
          <Text size='xs'>Date From/To</Text>
          <div className='flex justify-center gap-4 mt-2'>
            <Controller
              name='date'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <DatePickerInput selectedDate={field.value.startDate} isReadOnly wrapperClassName='!w-35' />
                  <ErrorMessage text={!field.value.startDate ? error?.message : undefined} />
                </div>
              )}
            />
            <Controller
              name='date'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <DatePickerInput selectedDate={field.value.endDate} isReadOnly wrapperClassName='!w-35' />
                  <ErrorMessage text={!field.value.endDate ? error?.message : undefined} />
                </div>
              )}
            />
          </div>
          <div className='flex  self-center mt-3'>
            <Controller
              name='date'
              control={control}
              rules={{
                validate: (value) => {
                  if (!value?.startDate) return ValidateUtils.requiredErrorMessage;
                  if (!value?.endDate) return ValidateUtils.requiredErrorMessage;
                  return true;
                },
              }}
              render={({ field }) => (
                <DateRangePickerInput
                  dateFrom={field.value.startDate}
                  dateTo={field.value.endDate}
                  maxDate={now}
                  onChange={(startDate, endDate) => field.onChange({ startDate, endDate })}
                />
              )}
            />
          </div>
        </div>
        {/* <div className='w-full flex flex-row dark:bg-soft-black p-4'>
          <Controller
            name='fileType'
            control={control}
            render={({ field }) => (
              <RadioInput
                orientation={RadioInputOrientationTypeEnum.Vertical}
                readonly
                options={FILE_TYPE_OPTIONS}
                selected={field.value}
                onChange={(data) => field.onChange(data == StatementExportFileType.Pdf ? StatementExportFileType.Pdf : StatementExportFileType.Excel)}
              />
            )}
          />
        </div> */}
      </div>
      <Button className='mt-7' color='primary' type='submit'>
        Generate
      </Button>
      {showErrorModal && (
        <ConfirmModal
          title={
            <>
              Something went wrong. <br /> Please try again later
            </>
          }
          icon={<AlertIcon />}
          onClick={() => setShowErrorModal(false)}
          buttonTitle='Back to the Statements page'
        />
      )}
    </form>
  );
};
